import { useState, useEffect, useRef } from 'react';

/**
 * Live countdown timer that ticks every second.
 *
 * Props:
 * - targetTime: Date | Firestore Timestamp | ISO string | epoch number
 * - onComplete: optional callback when countdown reaches zero
 *
 * Display format:
 * - > 24h:  "2d 5h 30m"
 * - > 1h:   "5h 12m 03s"
 * - > 1m:   "12m 45s"
 * - ≤ 1m:   "45s"
 * - ≤ 0:    "Starting soon..."
 */
function toDate(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000);
  }
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? null : d;
}

function formatCountdown(diffMs) {
  if (diffMs <= 0) return 'Starting soon...';

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, '0');

  if (days > 0) {
    return `${days}d ${hours}h ${pad(minutes)}m`;
  }
  if (hours > 0) {
    return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${pad(seconds)}s`;
  }
  return `${seconds}s`;
}

export default function Countdown({ targetTime, onComplete }) {
  const [now, setNow] = useState(Date.now());
  const completeFired = useRef(false);

  const target = toDate(targetTime);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fire onComplete callback once when countdown reaches zero
  useEffect(() => {
    if (!target) return;
    const diff = target.getTime() - now;
    if (diff <= 0 && onComplete && !completeFired.current) {
      completeFired.current = true;
      onComplete();
    }
  }, [now, target, onComplete]);

  if (!target) return null;

  const diff = target.getTime() - now;

  return <span className="countdown">{formatCountdown(diff)}</span>;
}
